import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, FileText, Video, MessageSquare } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

interface FloatingActionButtonProps {
  onCreatePost?: () => void;
}

export default function FloatingActionButton({ onCreatePost }: FloatingActionButtonProps) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    // Initialize position at center-right
    const updatePosition = () => {
      setPosition({
        x: window.innerWidth - 80,
        y: window.innerHeight / 2
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    const touch = e.touches[0];
    setStartPos({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();

    const touch = e.touches[0];
    const newX = Math.min(
      Math.max(16, touch.clientX - startPos.x),
      window.innerWidth - 72
    );
    const newY = Math.min(
      Math.max(16, touch.clientY - startPos.y),
      window.innerHeight - 72
    );

    setPosition({ x: newX, y: newY });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartPos({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;

    const newX = Math.min(
      Math.max(16, e.clientX - startPos.x),
      window.innerWidth - 72
    );
    const newY = Math.min(
      Math.max(16, e.clientY - startPos.y),
      window.innerHeight - 72
    );

    setPosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove as any);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove as any);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  const actions = [
    {
      icon: <MessageSquare className="h-5 w-5" />,
      label: 'Create Post',
      onClick: () => {
        setIsExpanded(false);
        onCreatePost?.();
      }
    },
    {
      icon: <FileText className="h-5 w-5" />,
      label: 'Write Article',
      onClick: () => {
        setIsExpanded(false);
        navigate('/write');
      }
    },
    {
      icon: <Video className="h-5 w-5" />,
      label: 'Upload Video',
      onClick: () => {
        setIsExpanded(false);
        navigate('/upload');
      }
    }
  ];

  return (
    <div
      className="fixed z-50 touch-none"
      style={{
        left: position.x,
        top: position.y
      }}
    >
      {/* Action Menu */}
      <div className={`
        absolute right-0 transition-all duration-200
        ${isExpanded ? 'scale-100 opacity-100' : 'scale-95 opacity-0 pointer-events-none'}
        ${position.y < window.innerHeight / 2 ? 'top-full mt-2' : 'bottom-full mb-2'}
      `}>
        <div className={`
          p-2 rounded-lg shadow-lg backdrop-blur-sm
          ${theme === 'dark'
            ? 'bg-gray-800/90 shadow-black/20'
            : 'bg-white/90 shadow-black/5'
          }
        `}>
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              className={`
                flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-left
                transition-colors whitespace-nowrap
                ${theme === 'dark'
                  ? 'hover:bg-gray-700/50 text-gray-200'
                  : 'hover:bg-gray-100/50 text-gray-700'
                }
              `}
            >
              {action.icon}
              <span className="font-medium">{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Button */}
      <button
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={() => !isDragging && setIsExpanded(!isExpanded)}
        className={`
          w-14 h-14 rounded-full shadow-lg flex items-center justify-center
          transition-all duration-200 cursor-move active:scale-95
          ${theme === 'dark'
            ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-500/20'
            : 'bg-blue-500 hover:bg-blue-600 text-white shadow-blue-600/20'
          }
          ${isDragging ? 'scale-95' : 'scale-100'}
        `}
      >
        {isExpanded ? (
          <X className="h-6 w-6" />
        ) : (
          <Plus className="h-6 w-6" />
        )}
      </button>
    </div>
  );
}